import { useState } from "react";
import { useLang } from "../lib/i18n.js";
import { platformInfo, useInstallPrompt } from "../lib/install.js";
import { CheckIcon } from "../ui/bits.js";

/**
 * /install: the page a customer or a driver lands on from "Install the app".
 * One button where the browser allows it, Safari's three taps where it does
 * not, and a way out of Telegram's in-app browser first.
 */

const LINK = "https://order.moomoofarms.com";

function ShareGlyph() {
  return (
    <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
    </svg>
  );
}

function PlusSquareGlyph() {
  return (
    <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}

function MenuGlyph() {
  return (
    <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="5.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="18.5" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function Install() {
  const { lang, t, toggle } = useLang();
  const info = platformInfo();
  // staff land here from the office page; they install the office app, which opens on /office
  const office = window.location.pathname.startsWith("/office") || new URLSearchParams(window.location.search).get("for") === "office";
  const home = office ? "/office" : "/";
  const { canPrompt, installed, prompt } = useInstallPrompt();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  async function install() {
    setBusy(true);
    try {
      await prompt();
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(LINK);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked: the link is printed right below the button anyway
    }
  }

  const done = info.standalone || installed;

  return (
    <div className="shell">
      <header className="top">
        <div className="brandrow">
          <img className="logo" src="/img/logo.png" alt="" width={42} height={42} />
          <h1>{t("brand")}</h1>
          <button type="button" className="lang" onClick={toggle}>
            {lang === "en" ? "ភាសាខ្មែរ" : "English"}
          </button>
        </div>
      </header>

      <div className="pad install">
        <div className="install-hero">
          <img src={office ? "/icon-office-192.png" : "/icon-192.png"} alt="" width={84} height={84} className="install-icon" />
          <h2>{office ? t("installOfficeTitle") : t("installTitle")}</h2>
          <p>{office ? t("installOfficeSub") : t("installSub")}</p>
        </div>

        {done ? (
          <div className="panel install-done">
            <span className="disc">
              <CheckIcon />
            </span>
            <p>{office ? t("installOfficeDone") : t("installDone")}</p>
            <a className="btn" href={home}>
              {office ? t("officeTitle") : t("installOpenShop")}
            </a>
          </div>
        ) : info.inApp ? (
          <div className="panel">
            <p className="install-lead">{t("installInApp")}</p>
            <ol className="steps">
              <li>
                <MenuGlyph />
                <span>{t("installInAppHow")}</span>
              </li>
            </ol>
            <button type="button" className="btn quiet" onClick={() => void copy()}>
              {copied ? t("installCopied") : t("installCopy")}
            </button>
          </div>
        ) : info.ios ? (
          <div className="panel">
            <ol className="steps">
              <li>
                <ShareGlyph />
                <span>{t("installIos1")}</span>
              </li>
              <li>
                <PlusSquareGlyph />
                <span>{t("installIos2")}</span>
              </li>
              <li>
                <CheckIcon />
                <span>{t("installIos3")}</span>
              </li>
            </ol>
          </div>
        ) : canPrompt ? (
          <div className="panel install-cta">
            <button type="button" className="btn" onClick={() => void install()} disabled={busy}>
              {t("installButton")}
            </button>
          </div>
        ) : (
          <div className="panel">
            <ol className="steps">
              <li>
                <MenuGlyph />
                <span>{t("installAndroidManual")}</span>
              </li>
            </ol>
          </div>
        )}

        {office ? (
          <div className="install-share">
            <p className="install-link">order.moomoofarms.com/office/install</p>
            <p className="note" style={{ margin: "4px 0 0" }}>{t("installOfficeNote")}</p>
          </div>
        ) : (
          <>
            <div className="install-share">
              <img src="/img/qr-order.png" alt={t("installQr")} width={168} height={168} />
              <p className="note" style={{ margin: "8px 0 0" }}>{t("installQr")}</p>
              <p className="install-link">order.moomoofarms.com</p>
              <button type="button" className="linkbtn" onClick={() => void copy()}>
                {copied ? t("installCopied") : t("installCopy")}
              </button>
            </div>
            <p className="note" style={{ textAlign: "center" }}>
              <a href="/office/install" className="linkbtn">
                {t("installStaff")}
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
